import { T } from '@transifex/react';
import React, { Suspense, useMemo, useState } from 'react';
import { pick } from 'lodash-es';
import { create } from 'zustand';
import dayjs from 'dayjs';
import { LoginShell } from 'src/layouts/LoginShell';
import { CitizenProvider } from 'src/context/citizen-context';
import { apiClient, apiClientMethods } from 'src/api/client';
import { applyAuthToken } from 'src/api/auth/session';
import {
  clearCitizenAuth,
  clearIdPortenSession,
  confirmIdPortenSession,
  isDevBypassAuth,
  isIdPortenSession,
} from 'src/api/state';
import { clearNotificationSession } from 'src/utils/notification-session';
import { useContainerNotificationsStore } from 'src/components/shared/container-notifications/store';
import { CriptioLogin } from 'src/components/CriptioLogin';
import { EmailLoginSurface } from 'src/components/login/EmailLoginSurface';
import { SuomiFiLogin } from 'src/components/login/SuomiFiLogin';
import { IdPortenLogin } from 'src/components/login/IdPortenLogin';
import { selectIdPortenLogoutUrl } from 'src/api/idportenLogout';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { LoadingPage } from '../components/shared';
import { useCompany } from './Company.store';
import { usePrincipalStore } from './Principal.store';

// Lazy-loaded so the ~657 LOC registration wizard (+ zod schema builder, hetu
// validator, registration mock) stays out of the eager first-paint bundle. It
// renders only behind the rare ?register=1 link or an explicit "create account"
// click. Suspense fallback covers the (fast) chunk load.
const RegistrationSurface = React.lazy(() =>
  import('src/components/registration/RegistrationSurface').then((m) => ({
    default: m.RegistrationSurface,
  }))
);

type TValue = {
  id: string;
  username: string;
  token: string;
  language: string;
  phone_number: string;
  email: string;
  name: string;
  anonymous: boolean;
};

type TStore = {
  data: TValue;
  logout: () => void;
  update: (v: Pick<TValue, 'language'>) => Promise<void>;
};

export const useUserStore = create<TStore>()(() => ({}) as TStore);

// Dev-only escape hatch: when the bypass flag is on (env var in dev or the
// localStorage toggle from the dev pill), force the user to appear
// non-anonymous so the app skips the MitID gate and falls through to the
// address-select + main app. Useful when you don't have a MitID to test
// tenants that are configured with login_method: 'user'.
const applyDevBypass = (data: TValue): TValue => {
  if (!isDevBypassAuth()) return data;
  return {
    ...data,
    anonymous: false,
  };
};

// exported for tests
export const augmentUser = (data: TValue): TValue => applyDevBypass(data);

export const useUser = () => {
  return useUserStore();
};

export const useUserMaybe = (): TValue | undefined => {
  return useUserStore().data;
};

/*
- getToken: from AUTH_TOKEN or from search param or from the api.
- getUser: using the token

- If LOGIN_METHOD_NONE:
  - set property user and OK.
- If LOGIN_METHOD_MIDID_POST_SEARCH:
  - If CheckIfPropertyUserIsAnanymous is true then redirect to mit id.
  - else set property user and OK.
- If LOGIN_METHOD_MIDID_PRE_SEARCH
  - set property user and OK.

*/

/**
 * Translator instruction carried into Transifex alongside the login-error
 * source strings. Avfall Sør is a Norwegian municipal portal, so the Norwegian
 * (nb) translations are the ones a citizen actually reads — an untranslated
 * English refusal is not acceptable there. The product name and the vendor ban
 * are Digdir contract terms, not style.
 */
const IDPORTEN_ERROR_COMMENT =
  'Shown to a citizen whose ID-porten login was refused. Norwegian (nb) is ' +
  'required — Avfall Sør is a Norwegian municipal portal. Keep the product ' +
  'name "ID-porten" untranslated, and never name an eID vendor (BankID, ' +
  "Buypass, Commfides, MinID): Digdir's terms of use forbid it.";

function getErrorJsx(
  error: {
    errorCode?: string;
    errorMessage?: string;
  },
  isIdPorten: boolean
): React.ReactNode {
  if (error?.errorCode === 'NO_PROPERTY_SELECTED') {
    return (
      <T _str="No property was selected. Please go back and select a property before logging in." />
    );
  }
  // The single most likely ID-porten refusal: the citizen authenticated fine,
  // but their national ID matches no PropertyUser and no active PropertyContact
  // `tax_id` on this tenant.
  //
  // Two messages, because the two flows differ in what the citizen has already
  // done. ID-porten authenticates BEFORE any address is chosen
  // (`post_auth_search`), so there is no property in context and naming one is
  // incoherent; the refusal is about the person, not the address. The Criipto
  // tenants run `pre_auth_search`, where an address IS selected first — that
  // string stays exactly as it was, keeping its Transifex key.
  //
  // Both stay eID-agnostic: Digdir's terms forbid naming BankID and the rest on
  // an ID-porten surface.
  if (error?.errorCode === 'USER_NOT_REGISTERED') {
    if (isIdPorten) {
      return (
        <T
          _str="We could not find you as a resident in this service area. Please contact customer service if you believe this is an error."
          _comment={IDPORTEN_ERROR_COMMENT}
        />
      );
    }
    return (
      <T _str="We could not verify your identity for this property. Please try again, or contact your municipality if the issue persists." />
    );
  }
  if (error?.errorCode === 'INACTIVE_CPR') {
    return (
      <T _str="Your CPR number is not active. Please contact your municipality if you believe this is an error." />
    );
  }
  // ID-porten refusals. The backend hands these back on the same
  // `?error_code=` channel as the Criipto ones (PortalHandoffMixin), so they
  // land here rather than anywhere ID-porten-specific.
  //
  // ID-porten answered with a weaker assurance level than the tenant's
  // integration asks for, and the backend refused the login server-side.
  // Avfall Sør is provisioned at `idporten-loa-substantial`, which in practice
  // means the citizen picked an eID or a path that only reaches `-low`.
  if (error?.errorCode === 'IDPORTEN_LOA_NOT_MET') {
    return (
      <T
        _str="Your electronic ID did not reach the security level this service requires. Please log in through ID-porten again and choose an electronic ID at a higher security level."
        _comment={IDPORTEN_ERROR_COMMENT}
      />
    );
  }
  // The login link was used twice — a browser back-navigation onto the
  // hand-back URL, a replayed link, or a stale tab. Never a reason to alarm the
  // citizen; ask them to start over.
  if (error?.errorCode === 'SESSION_ALREADY_COMPLETED') {
    return (
      <T
        _str="This login link has already been used. Please log in again."
        _comment={IDPORTEN_ERROR_COMMENT}
      />
    );
  }
  if (error?.errorMessage) {
    return error.errorMessage;
  }
  return <T _str="An unknown error occurred" />;
}

export const UserStoreProvider = ({
  children,
}: {
  children: JSX.Element;
}): JSX.Element => {
  const { data: company, hostname } = useCompany();
  const { data: user } = useUser();
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const [error, setError] = useState<{
    errorCode?: string;
    errorMessage?: string;
  }>({});

  const logout = () => {
    // Read the live credential BEFORE clearCitizenAuth drops it: the ID-porten
    // logout round-trip below identifies the session by `api_key`, and there is
    // no second chance once localStorage is cleared.
    const sessionToken = localStorage.getItem('AUTH_TOKEN');
    const cameFromIdPorten = isIdPortenSession();

    clearCitizenAuth();
    // The verified phone/email session lives in cookies, which outlived logout
    // and greeted the next user of this browser with the previous one's number.
    clearNotificationSession();
    // BEACON-57 / PRO-674: reminder edits the citizen never confirmed are held
    // in memory, not in those cookies, and logging out must take them with it —
    // an unsaved change may not outlive the session that could have saved it,
    // and on a shared device it must not be sitting there for the next person
    // to press Save on.
    useContainerNotificationsStore.getState().reset();
    // Clears ACTIVE_PRINCIPAL_ID + the X-On-Behalf-Of request header + the
    // reactive store, so a shared-device next-user never inherits the acting-as
    // selection of the session that just ended.
    usePrincipalStore.getState().setActive(null);
    window.history.replaceState(
      {},
      document.title,
      window.WH_SS_INIT_ROUTE || '/'
    );

    // ID-porten keeps a central SSO session at Digdir that a local clear does
    // not touch, so a "logged out" citizen could press login and be back in
    // without re-authenticating — the next person on a shared device inherits
    // the session. Hand the browser to the backend's SP-initiated logout, which
    // ends Digdir's session and returns here carrying no credential.
    //
    // Only for a session this portal actually established through the ID-porten
    // hand-back (isIdPortenSession). `anonymous: false` is not proof of that:
    // the admin platform's "Open portal" link mints a machine PropertyUser that
    // is non-anonymous and never saw ID-porten, and the backend answers such a
    // token with a dead-end "No active ID-porten session to log out." page.
    // The anonymous boot key is likewise excluded, and so is the dev-bypass
    // session — it FORCES `anonymous: false` onto a key that never saw
    // ID-porten (see applyDevBypass).
    const idPortenLogoutUrl =
      cameFromIdPorten && !isDevBypassAuth()
        ? selectIdPortenLogoutUrl(
            company?.authentication_urls,
            sessionToken,
            window.location.origin
          )
        : null;
    if (idPortenLogoutUrl) {
      // assign(), not reload() — the local state is already gone, and the
      // backend answers 302 to Digdir's end_session_endpoint, which has to run
      // top-level to carry (and kill) the SSO cookie.
      window.location.assign(idPortenLogoutUrl);
      return;
    }

    window.location.reload();
  };

  const update: TStore['update'] = async (values) => {
    await apiClient
      .post(`property_user/`, {
        ...pick(user, ['name', 'phone_number', 'email', 'language']),
        ...values,
      })
      .then((d) => {
        useUserStore.setState({ data: d.data });
      });
  };

  const { isLoading } = useQuery({
    queryKey: ['UserStoreProvider', 'Get'],
    // The queryFn IS the login flow (token handling + side effects) — keep
    // reconnect/remount refetches from re-running it back-to-back.
    staleTime: 60_000,
    queryFn: async () => {
      // if there is error then show error and return
      const errorCode = searchParams.get('error_code') || undefined;
      const errorMessage = searchParams.get('error') || undefined;
      if (errorCode || errorMessage) {
        // This hand-back refused the login, so the attempt it answers must not
        // stay pending: leaving it would let the next unrelated ?api_key=
        // hand-back on this browser promote it into an ID-porten session.
        clearIdPortenSession();
        setError({
          errorCode,
          errorMessage,
        });
        return null;
      }

      // get the token
      const token =
        searchParams.get('api_key') || localStorage.getItem('AUTH_TOKEN');

      if (token) {
        // need to remove the api_key from the url once we used it.
        if (searchParams.get('api_key')) {
          const propertyId = searchParams.get('property_key');
          // also checking for property key here
          if (propertyId) {
            localStorage.setItem('AUTH_PROPERTY_ID', propertyId as string);
            localStorage.setItem(
              'AUTH_TOKEN_EXPIRY',
              dayjs().add(60, 'minutes').toISOString()
            );
          }
          // The hand-back completes an ID-porten login this portal started, so
          // promote the pending mark to a confirmed record. Only then does
          // logout() end Digdir's central SSO session.
          confirmIdPortenSession();
          window.history.replaceState({}, document.title, '/');
        }

        apiClientMethods.setToken(token);
        return apiClient
          .get('property_user/')
          .then((d) => {
            if (d.data.id) {
              useUserStore.setState({
                data: augmentUser(d.data),
                logout,
                update,
              });
              localStorage.setItem('AUTH_TOKEN', token);
              // Every persisted token needs an expiry or the session-timeout
              // interceptor treats the session as timed out (the ?api_key=
              // login path only writes one when property_key is present).
              // Guarded so an existing window isn't extended on every boot.
              if (!localStorage.getItem('AUTH_TOKEN_EXPIRY')) {
                localStorage.setItem(
                  'AUTH_TOKEN_EXPIRY',
                  dayjs().add(60, 'minutes').toISOString()
                );
              }
            }
            return null;
          })
          .catch((e) => {
            setError({
              errorMessage: e?.message || <T _str="Something went wrong" />,
            });
            return null;
          });
      }
      // if there is no token then fetch it using company login method.
      return apiClient
        .post(`company/${hostname}/login`, { username: '', password: '' })
        .then((d) => {
          if (d.data.id) {
            applyAuthToken((d.data as TValue).token);
            useUserStore.setState({
              data: augmentUser(d.data),
              logout,
              update,
            });
          }
          return null;
        })
        .catch((e) => {
          if (e?.response?.status === 404) {
            // wrong property selected. Not authorized. invalid token
            logout();
          }
          return null;
        });
    },
  });

  if (isLoading) {
    return <LoadingPage />;
  }

  // A registration link (?register=1) opens the public sign-up surface for a
  // prospect with no account yet. Login-method-independent; scrub the param
  // immediately (as ?api_key= / ?reset_token= do) so it can't linger in history.
  const registerFlag = searchParams.get('register') === '1';
  if (registerFlag) {
    window.history.replaceState({}, document.title, window.location.pathname);
    return (
      <LoginShell title={<T _str="Create your account" />}>
        <Suspense fallback={<LoadingPage />}>
          <RegistrationSurface onDone={() => window.location.assign('/')} />
        </Suspense>
      </LoginShell>
    );
  }

  if (user) {
    if (company.login_method === 'email_otp' && user.anonymous) {
      return (
        <LoginShell>
          <EmailLoginSurface />
        </LoginShell>
      );
    }
    // Suomi.fi (FTN). Live as soon as the tenant carries a Suomi.fi entry in
    // `authentication_urls`; SuomiFiLogin falls back to a disabled placeholder
    // when it does not. Also reachable via the dev-pill "Login method"
    // override for tenants still on another login_method.
    if (company.login_method === 'suomifi' && user.anonymous) {
      return (
        <LoginShell title={<T _str="Sign in with Suomi.fi" />}>
          <SuomiFiLogin />
        </LoginShell>
      );
    }
    // ID-porten (Digdir), Norway's public-sector eID gateway. Routed exactly as
    // 'bankid' is — it honours `login_method_user`, so this branch is the
    // `post_auth_search` half (log in, then pick an address) and
    // Property.store owns the `pre_auth_search` half (pick an address, then log
    // in, carrying `property_id` into the authorize URL). Both halves the
    // backend supports; which one Avfall Sør uses is tenant config.
    //
    // It gets its own surface rather than a CriptioLogin branch because
    // CriptioLogin is the Criipto broker's antd multi-provider picker, whose
    // unmatched-provider fallback paints NemID branding — forbidden here by
    // Digdir's terms. See IdPortenLogin.
    if (
      company.login_method === 'idporten' &&
      company.login_method_user === 'post_auth_search' &&
      user.anonymous
    ) {
      return (
        <LoginShell>
          <IdPortenLogin />
        </LoginShell>
      );
    }
    if (
      (company.login_method === 'user' || company.login_method === 'bankid') &&
      company.login_method_user === 'post_auth_search' &&
      user.anonymous
    ) {
      // show mit id login here.
      return (
        <LoginShell>
          <CriptioLogin />
        </LoginShell>
      );
    }
    return <CitizenProvider>{children}</CitizenProvider>;
  }

  return (
    <LoginShell>
      <div className="flex w-full flex-col items-center gap-5 rounded-lg bg-secondary p-10 text-center">
        <p className="text-base text-muted-foreground">
          {getErrorJsx(error, company?.login_method === 'idporten')}
        </p>
        <Button onClick={logout}>
          <T _str="Back to Login" />
        </Button>
      </div>
    </LoginShell>
  );
};
